import { ContentCalendarPrimaryGroup, CalendarPrimaryGroupName, ContentCalendarMatchedResults, ContentCalendarWeekMatch, ContentCalendarItemMatch } from "@contracts/contentCalendar";
import SocialIntegrationRepo from "@repos/SocialIntegrationRepo";
import { MetricSender } from "@helpers/MetricSender";
import { isCurrentCalendarTest } from "@logic/calendarManager";
import router from "@/router";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import DayjsExtended from "@logic/DayjsExtended";

const SPECIFIC_DATE_MIDDAY = "T12:00";

/** Scheduled local time, else specificDate at midday, else null (undated). */
export function getCalendarItemSortDateKey(item: ContentCalendarItemMatch): string | null {
  const localDateTime = item.socialPost?.scheduledToPostAt?.localDateTime;
  if (localDateTime) {
    return DayjsExtended(localDateTime).format("YYYY-MM-DDTHH:mm");
  }
  const specificDate = item.calendarItem?.specificDate?.trim();
  if (specificDate) {
    return `${specificDate}${SPECIFIC_DATE_MIDDAY}`;
  }
  return null;
}

/** Dated items first (by schedule / specificDate), then undated by unused order. */
export function compareCalendarItemsBySchedule(a: ContentCalendarItemMatch, b: ContentCalendarItemMatch): number {
  const aKey = getCalendarItemSortDateKey(a);
  const bKey = getCalendarItemSortDateKey(b);
  if (aKey && bKey) return aKey.localeCompare(bKey);
  if (aKey) return -1;
  if (bKey) return 1;
  return (a.calendarItem?.order ?? 0) - (b.calendarItem?.order ?? 0);
}

export interface MatchedResultObjBucketPath {
  primaryGroup: CalendarPrimaryGroupName;
  weekIndex: number;
  bucketIndex: number;
}

export const SYNC_ACCOUNTS_KEY = "sync";
export const LOCATIONS_SECTION_HASH = "locations";
export const ATLAS_SETTINGS_HASH = "atlas-settings";

export default {
  // Finds a calendar item or a post's primary group, week and bucket indexes ======
  findBucketPath(matchedResults: ContentCalendarMatchedResults, itemOrPostOrMatchId: string): MatchedResultObjBucketPath {
    const matchedResultObjBucketPath: Partial<MatchedResultObjBucketPath> = {};
    const primaryGroupNames = Object.values(CalendarPrimaryGroupName).filter(value => typeof value === "string");

    for (let i = 0; i < primaryGroupNames.length; i++) {
      for (const [weekIndex, weekObj] of (matchedResults[primaryGroupNames[i]] as ContentCalendarPrimaryGroup).weeks.entries()) {
        for (const [bucketIndex, bucket] of weekObj.items.entries()) {
          const contentMatchIds = bucket.calendarItem?.contentSourceMatches?.map(match => match.contentSummaryId) ?? [];
          if ([bucket.calendarItem?.id, bucket.socialPost?.id, ...contentMatchIds].includes(itemOrPostOrMatchId)) {
            matchedResultObjBucketPath.primaryGroup = primaryGroupNames[i];
            matchedResultObjBucketPath.weekIndex = weekIndex;
            matchedResultObjBucketPath.bucketIndex = bucketIndex;
            break;
          }
        }
      }
    }

    return matchedResultObjBucketPath as MatchedResultObjBucketPath;
  },

  // Finds a week path by weekKey, or creates a new empty week in Upcoming if not found
  // This is useful when a post is rescheduled to a week that wasn't originally returned by the server
  findWeekPath(matchedResults: ContentCalendarMatchedResults, weekKey: string): MatchedResultObjBucketPath {
    // First try to find the existing week in any primary group
    const primaryGroupNames = Object.values(CalendarPrimaryGroupName).filter(value => typeof value === "string");

    for (let i = 0; i < primaryGroupNames.length; i++) {
      const weeks = (matchedResults[primaryGroupNames[i] as keyof ContentCalendarMatchedResults] as ContentCalendarPrimaryGroup).weeks;
      for (const [weekIndex, weekObj] of weeks.entries()) {
        if (weekObj.weekGroup === weekKey) {
          return {
            primaryGroup: primaryGroupNames[i] as unknown as CalendarPrimaryGroupName,
            weekIndex,
            bucketIndex: weekObj.items.length,
          };
        }
      }
    }

    // Week not found - create a new one in Upcoming
    const remoteLogger = getGlobalRemoteLogger();
    remoteLogger.info(`ContentCalendarHelper.findWeekPath: Creating new week bucket for weekKey=${weekKey}`);

    // Create the new week structure
    const weekDate = DayjsExtended(weekKey);
    const newWeek: ContentCalendarWeekMatch = {
      weekGroup: weekKey,
      weekGroupName: weekDate.format("MMMM Do"),
      userGuidance: null,
      systemTopics: null,
      items: [],
    };

    // Find the correct insertion point in Upcoming (sorted chronologically)
    const targetWeeks = matchedResults[CalendarPrimaryGroupName.Upcoming].weeks;
    let insertIndex = targetWeeks.length; // Default to end

    for (let i = 0; i < targetWeeks.length; i++) {
      const existingWeekDate = DayjsExtended(targetWeeks[i].weekGroup);
      if (weekDate.isBefore(existingWeekDate)) {
        insertIndex = i;
        break;
      }
    }

    // Insert the new week at the correct position
    targetWeeks.splice(insertIndex, 0, newWeek);

    return {
      primaryGroup: CalendarPrimaryGroupName.Upcoming,
      weekIndex: insertIndex,
      bucketIndex: 0, // New week has no items yet
    };
  },

  // Link more social accounts ==================================================
  async linkMoreAccounts(customerId: string, page = "posts", selectedPlatforms: ReadonlyArray<string> = [], options?: { forceStandardScope?: boolean }): Promise<void> {
    const remoteLogger = getGlobalRemoteLogger();

    if (await isCurrentCalendarTest(customerId)) {
      alert("Connecting Accounts is disabled for test calendars");
      return;
    }

    MetricSender.sendMetric("Cust-Calendar-Connect-Accounts", { customerId, page });

    // Always return to CalendarPosts route with sync hash
    const routeLocation = router.resolve({ name: "CalendarPosts", params: { customerId }, hash: `#${SYNC_ACCOUNTS_KEY}` });
    const returnUrl = `${globalThis.location.origin}${routeLocation.href}`;

    remoteLogger.info(
      `ContentCalendarHelper.linkMoreAccounts: Redirecting to portal. customerId=${customerId}, page=${page}, returnUrl=${returnUrl}, selectedPlatforms=${selectedPlatforms.join(",")}, forceStandardScope=${options?.forceStandardScope === true}`
    );

    const portalUrl = await SocialIntegrationRepo.createPortalLink(customerId, returnUrl, selectedPlatforms, options);
    globalThis.location.href = portalUrl;
  },
};
